import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import { getPasswordValidationErrors } from '@/lib/passwordValidation'

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  onLogin: (email: string, password: string) => Promise<void>
  onRegister: (email: string, password: string, name: string) => Promise<void>
  onSocialLogin: (provider: 'google' | 'apple', credential?: string) => Promise<void>
}

export default function AuthModal({ 
  isOpen, 
  onClose, 
  onLogin, 
  onRegister, 
  onSocialLogin 
}: AuthModalProps) {
  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (!isOpen) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prevOverflow }
  }, [isOpen])

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      if (mode === 'login') {
        await onLogin(email, password)
      } else {
        const passwordErrors = getPasswordValidationErrors(password)
        if (passwordErrors.length > 0) {
          setError(`Password must contain: ${passwordErrors.join(', ')}`)
          return
        }
        if (password !== confirmPassword) {
          setError('Passwords do not match')
          return
        }
        const fullName = `${firstName} ${lastName}`.trim()
        await onRegister(email, password, fullName)
      }
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed')
    } finally {
      setLoading(false)
    }
  }

  const handleSocialLogin = async (provider: 'google' | 'apple', credential?: string) => {
    setLoading(true)
    setError('')

    try {
      if (provider === 'google' && credential) {
        // Pass the Google credential to the parent component
        await onSocialLogin(provider, credential)
      } else {
        await onSocialLogin(provider)
      }
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Social login failed')
    } finally {
      setLoading(false)
    }
  }

  const modal = (
        <div className="fixed inset-0 flex items-center justify-center auth-overlay" style={{ zIndex: 1000 }}>
          <div 
            className="card auth-card"
            style={{
              maxWidth: '360px',
              width: '90%',
              maxHeight: '85vh',
              overflow: 'auto',
              padding: '20px',
              position: 'relative'
            }}
          >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200"
          style={{
            color: 'var(--text-muted)',
            backgroundColor: 'transparent'
          }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="text-center mb-5">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{
            background: 'linear-gradient(135deg, var(--brand-primary) 0%, #0a5230 100%)',
            boxShadow: '0 4px 15px rgba(11, 77, 42, 0.3)'
          }}>
            <svg className="w-6 h-6" style={{ color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-4a2 2 0 00-2-2H6a2 2 0 00-2 2v4a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold gradient-text mb-1">
            {mode === 'login' ? 'Sign In to Bid' : 'Create Account to Bid'}
          </h2>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Join our marketplace to place bids on authentic items
          </p>
        </div>

        {/* Social Login Buttons */}
        <div className="space-y-2 mb-4">
          <div className="w-full rounded-lg overflow-hidden" style={{
            display: 'flex',
            justifyContent: 'center'
          }}>
            <div style={{ width: '100%', minWidth: '100%' }}>
              <GoogleLogin
                onSuccess={(credentialResponse: CredentialResponse) => {
                  handleSocialLogin('google', credentialResponse.credential)
                }}
                onError={() => {
                  setError('Google login failed')
                }}
                useOneTap={false}
                text="continue_with"
                theme="outline"
                size="large"
                shape="pill"
                width="100%"
                logo_alignment="left"
              />
            </div>
          </div>

          <button
            onClick={() => handleSocialLogin('apple')}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border text-sm"
            style={{
              backgroundColor: 'var(--text-primary)',
              borderColor: 'var(--text-primary)',
              color: 'var(--background-primary)'
            }}
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            Continue with Apple
          </button>
        </div>

        {/* Divider */}
        <div className="mb-4">
          <div className="flex items-center w-full text-sm">
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border-primary)' }}></div>
            <span className="px-2" style={{ color: 'var(--text-muted)' }}>
              or continue with email
            </span>
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border-primary)' }}></div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-3 p-3 rounded-lg" style={{ 
            backgroundColor: 'color-mix(in srgb, var(--accent-danger) 10%, transparent)',
            borderColor: 'color-mix(in srgb, var(--accent-danger) 20%, transparent)',
            border: '1px solid'
          }}>
            <p className="text-xs" style={{ color: 'var(--accent-danger)' }}>{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-3">
          {mode === 'register' && (
            <>
              <Input
                type="text"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
              <Input
                type="text"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </>
          )}
          
          <Input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          
          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L12 12m-3.122-3.122A7.965 7.965 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>

          {mode === 'register' && (() => {
            const unmetRequirements = getPasswordValidationErrors(password)
            return unmetRequirements.length > 0 ? (
              <div className="mt-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                Password must contain:
                <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
                  {unmetRequirements.includes('At least 8 characters') && <li>At least 8 characters</li>}
                  {unmetRequirements.includes('One lowercase letter') && <li>One lowercase letter</li>}
                  {unmetRequirements.includes('One uppercase letter') && <li>One uppercase letter</li>}
                  {unmetRequirements.includes('One number') && <li>One number</li>}
                  {unmetRequirements.includes('One special character (@$!%*?&)') && <li>One special character (@$!%*?&)</li>}
                </ul>
              </div>
            ) : null
          })()}

          {mode === 'register' && (
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full"
          >
            {loading 
              ? (mode === 'login' ? 'Signing in...' : 'Creating account...')
              : (mode === 'login' ? 'Sign In' : 'Create Account')
            }
          </Button>
        </form>

        {/* Toggle Mode */}
        <div className="text-center mt-4">
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            {mode === 'login' ? "Don't have an account? " : "Already have an account? "}
            <button
              onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              className="font-medium link-text text-xs"
              disabled={loading}
            >
              {mode === 'login' ? 'Create one' : 'Sign in'}
            </button>
          </p>
        </div>
          </div>
        </div>
  )

  // Render via portal to avoid sticky header/backdrop-filter stacking issues
  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modal, document.body)
  }
  return modal
}
